import React, { useState, useMemo, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import StaticSeo from '../components/StaticSeo.jsx';
import JsonLd from '../components/JsonLd.jsx';
import { hardnessColor, mgLToGpg } from '../data/water.js';
import { FIELD_GUIDE } from '../data/waterPanel.js';
import { WATER_RANKINGS as R } from '../data/waterRankings.js';
// s136: the map is its own chunk (geometry ≈26 KB) so the tables render first.
const RankingsMap = lazy(() => import('../components/RankingsMap.jsx'));

// ─────────────────────────────────────────────────────────────────────────
// /water/rankings — Phase 5b (s134). City-by-city rankings of what each
// utility itself publishes: hardness, total dissolved solids, and which
// cities disinfect with chloramine.
//
// Same constraint as /water: the complete tables are the DEFAULT render, so a
// crawler or a reader with JS off gets every ranked city. The sort buttons
// only reorder rows that are already on the page.
//
// Every number comes from src/data/waterRankings.js, which is GENERATED from
// the city modules (scripts/build-water-rankings.mjs) and gated
// (scripts/verify-water-rankings.mjs). This file computes nothing that the
// generator did not already rank; it only counts and formats. Band labels and
// TDS flags were set in the generator from water.js / waterPanel.js, so no
// threshold lives in this component.
// ─────────────────────────────────────────────────────────────────────────

const SITE = 'https://windowwashingguide.com';
const PATH = '/water/rankings';
// Written by scripts/build-water-rankings.mjs (public/data/), gated for drift by verify-water-rankings.
const CSV_URL = '/data/wwg-city-water.csv';

// Ranks (competition style: equal figures share one) come from the generated data,
// so this page and the /water/<state> roll-ups print the same number.

const SORTS = {
  typical: { label: 'TYPICAL, HIGH FIRST', fn: (a, b) => b.v - a.v || a.c.localeCompare(b.c) },
  low: { label: 'TYPICAL, LOW FIRST', fn: (a, b) => a.v - b.v || a.c.localeCompare(b.c) },
  hi: { label: 'HIGHEST PUBLISHED', fn: (a, b) => b.hi - a.hi || a.c.localeCompare(b.c) },
  lo: { label: 'LOWEST PUBLISHED', fn: (a, b) => a.lo - b.lo || a.c.localeCompare(b.c) },
};

const cityHref = (r) => `/states/${r.s}/${r.u}`;
const BASIS_MARK = { figure: '', average: 'avg', midpoint: 'mid' };

export default function WaterRankings() {
  const [dark, setDark] = useState(false);
  const [hSort, setHSort] = useState('typical');
  const [tSort, setTSort] = useState('typical');

  const T = dark
    ? { bg: '#1A1D1C', surface: '#22262A', ink: '#F4F1EA', inkSoft: '#B8B2A4', inkFaint: '#6B6960', rule: '#2E3236', sienna: '#D67148', sage: '#9CA89A' }
    : { bg: '#F4F1EA', surface: '#FAF7F1', ink: '#1A1D1C', inkSoft: '#5A5A52', inkFaint: '#A39E91', rule: '#D9D3C5', sienna: '#B5532A', sage: '#9CA89A' };
  const fontDisplay = "'Fraunces', serif";
  const fontBody = "'Inter Tight', sans-serif";
  const fontMono = "'JetBrains Mono', monospace";

  const hardRanked = R.hardness;
  const tdsRanked = R.tds;
  const hRows = useMemo(() => (hSort === 'typical' ? hardRanked : [...hardRanked].sort(SORTS[hSort].fn)), [hSort, hardRanked]);
  const tRows = useMemo(() => (tSort === 'typical' ? tdsRanked : [...tdsRanked].sort(SORTS[tSort].fn)), [tSort, tdsRanked]);

  const nr = R.hardnessNotRanked;
  const hardest = hardRanked[0];
  const softest = hardRanked[hardRanked.length - 1];
  const topTen = hardRanked.slice(0, 10);
  const bottomTen = [...hardRanked].reverse().slice(0, 10);
  const midpointCount = R.hardness.filter((r) => r.basis === 'midpoint').length;
  const tdsOver = R.tds.filter((r) => r.flag === FIELD_GUIDE.tds.rules[0].flag).length;
  const tdsClean = R.tds.filter((r) => r.flag === FIELD_GUIDE.tds.rules[1].flag).length;
  const phHigh = R.ph.filter((r) => r.flag === FIELD_GUIDE.ph.rules[0].flag).length;
  const lsiDeposit = R.langelier.filter((r) => r.tendency === 'deposits scale').length;
  const lsiDissolve = R.langelier.filter((r) => r.tendency === 'dissolves scale').length;
  const lsiEither = R.langelier.filter((r) => r.tendency === 'either way').length;
  const chloramineStates = new Set(R.chloramine.map((r) => r.s)).size;
  const chlorByState = useMemo(() => {
    const m = new Map();
    for (const r of R.chloramine) { if (!m.has(r.st)) m.set(r.st, []); m.get(r.st).push(r); }
    return [...m.entries()];
  }, []);
  const freeChlorineFamily = Object.entries(R.disinfectantCounts)
    .filter(([k]) => !/chloramine|seasonal switch|not published/.test(k))
    .reduce((a, [, n]) => a + n, 0);

  const datasetLd = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'Dataset',
    name: 'US city water rankings — published hardness, total dissolved solids and chloramine use',
    description: `Water hardness (mg/L as CaCO₃) ranked for the ${R.hardness.length} of ${R.cities} US cities whose utility publishes a figure, total dissolved solids ranked for ${R.tds.length} cities, and the disinfectant used in all ${R.cities}, each taken from the operating utility's own water-quality report.`,
    url: `${SITE}${PATH}`,
    isPartOf: { '@type': 'Dataset', name: 'US Water Hardness Atlas', url: `${SITE}/water` },
    keywords: ['hardest water by city', 'softest water by city', 'total dissolved solids', 'chloramine', 'water hardness', 'United States'],
    license: `${SITE}/editorial-standards`,
    isAccessibleForFree: true,
    creator: { '@type': 'Organization', name: 'Window Washing Guide', url: SITE },
    spatialCoverage: { '@type': 'Place', name: 'United States' },
    measurementTechnique: 'Figures transcribed from each utility\'s Consumer Confidence Report or published water-quality data; ranked on the single published figure, else the utility\'s published average, else the midpoint of the published range (marked).',
    distribution: [{ '@type': 'DataDownload', encodingFormat: 'text/csv', contentUrl: `${SITE}${CSV_URL}` }],
    variableMeasured: [
      { '@type': 'PropertyValue', name: 'Total hardness', unitText: 'mg/L as CaCO₃' },
      { '@type': 'PropertyValue', name: 'Total dissolved solids', unitText: 'mg/L' },
      { '@type': 'PropertyValue', name: 'pH' },
      { '@type': 'PropertyValue', name: 'Langelier saturation index' },
      { '@type': 'PropertyValue', name: 'Residual disinfectant', description: 'Free chlorine or chloramine, as the utility reports it' },
    ],
  });
  const breadcrumbLd = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Guide', item: `${SITE}/` },
      { '@type': 'ListItem', position: 2, name: 'Water Atlas', item: `${SITE}/water` },
      { '@type': 'ListItem', position: 3, name: 'City rankings', item: `${SITE}${PATH}` },
    ],
  });

  const h2 = { fontFamily: fontDisplay, fontSize: 30, fontWeight: 400, letterSpacing: '-0.02em', margin: '0 0 8px' };
  const para = { fontFamily: fontBody, fontSize: 15, lineHeight: 1.65, color: T.inkSoft, margin: '0 0 14px', maxWidth: 660 };
  const th = { textAlign: 'left', padding: '8px 10px 8px 0', fontFamily: fontMono, fontSize: 10, letterSpacing: '0.12em', color: T.inkFaint, fontWeight: 400, borderBottom: `1px solid ${T.rule}` };
  const td = { padding: '7px 10px 7px 0', borderTop: `1px solid ${T.rule}`, verticalAlign: 'top', fontSize: 14 };
  const link = { color: T.sienna };

  const SortBar = ({ value, onChange, label }) => (
    <div role="group" aria-label={label} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '0 0 14px' }}>
      {Object.entries(SORTS).map(([k, s]) => (
        <button key={k} onClick={() => onChange(k)} aria-pressed={value === k} style={{
          background: 'transparent', cursor: 'pointer', fontFamily: fontMono, fontSize: 10, letterSpacing: '0.12em',
          padding: '6px 10px', borderRadius: 2, color: value === k ? T.sienna : T.inkSoft,
          border: `1px solid ${value === k ? T.sienna : T.rule}`,
        }}>{s.label}</button>
      ))}
    </div>
  );

  // 'avg' carries the average itself: the row is ranked on it, so the reader
  // has to be able to see it. 'mid' carries no number, because the midpoint
  // is our sort key and printing it would look like a published figure.
  const Mark = ({ basis, v }) => BASIS_MARK[basis]
    ? <span title={basis === 'midpoint' ? 'Ranked at the midpoint of the published range' : 'Ranked on the utility\'s published average'} style={{ fontFamily: fontMono, fontSize: 9.5, letterSpacing: '0.08em', color: T.inkFaint, marginLeft: 6 }}>{basis === 'average' && v != null && !/\(/.test(String(v)) ? `${BASIS_MARK[basis]} ${v}` : BASIS_MARK[basis]}</span>
    : null;

  const NameList = ({ rows, word }) => (
    <p data-region="rank-unranked" style={{ ...para, fontSize: 14 }}>
      {rows.map((r, i) => (
        <React.Fragment key={`${r.s}/${r.u}`}>
          {i > 0 && ' · '}
          <Link to={cityHref(r)} style={{ color: T.inkSoft }}>{r.c}, {r.st}</Link>
          {word && r.word ? <span style={{ color: T.inkFaint }}> ({r.word})</span> : null}
        </React.Fragment>
      ))}
    </p>
  );

  return (
    <div style={{ minHeight: '100vh', background: T.bg, color: T.ink, fontFamily: fontBody, transition: 'background 0.3s, color 0.3s' }}>
      <StaticSeo path={PATH} />
      <JsonLd payload={datasetLd} />
      <JsonLd payload={breadcrumbLd} />
      <style>{`
        .wwg-rank-row:hover { background: ${T.surface}; }
        @media (max-width: 640px) { .wwg-rank-opt { display: none; } }
      `}</style>

      <div style={{ maxWidth: 860, margin: '0 auto', padding: '32px 24px 80px' }}>
        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', paddingBottom: 16, borderBottom: `1px solid ${T.rule}`, marginBottom: 32 }}>
          <Link to="/" style={{ fontFamily: fontDisplay, fontSize: 22, letterSpacing: '-0.01em', color: T.ink, textDecoration: 'none' }}>
            Window Washing <em style={{ fontStyle: 'italic' }}>Guide</em>
          </Link>
          <button onClick={() => setDark(!dark)} aria-label={dark ? 'Switch to light theme' : 'Switch to dark theme'} style={{
            background: 'transparent', border: `1px solid ${T.rule}`, borderRadius: '50%', width: 32, height: 32,
            color: T.inkSoft, cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>{dark ? '☀' : '☾'}</button>
        </header>

        <nav aria-label="Breadcrumb" style={{ fontFamily: fontMono, fontSize: 11, letterSpacing: '0.15em', color: T.inkFaint, marginBottom: 36 }}>
          <Link to="/" style={{ color: T.inkFaint, textDecoration: 'none' }}>GUIDE</Link>{' / '}
          <Link to="/water" style={{ color: T.inkFaint, textDecoration: 'none' }}>WATER ATLAS</Link>{' / '}CITY RANKINGS
        </nav>

        <span style={{ fontFamily: fontMono, fontSize: 11, letterSpacing: '0.18em', color: T.sienna }}>REFERENCE DATA · {R.cities} CITIES</span>
        <h1 style={{ fontFamily: fontDisplay, fontSize: 46, fontWeight: 400, lineHeight: 1.05, letterSpacing: '-0.025em', margin: '12px 0 16px' }}>
          Hardest and softest tap water, city by city
        </h1>
        <p style={{ fontFamily: fontDisplay, fontSize: 20, fontWeight: 300, lineHeight: 1.5, color: T.inkSoft, margin: '0 0 24px' }}>
          Ranked from what each city's water utility prints in its own report, not from a national model.
          Where a utility prints nothing, the city is listed as unranked rather than guessed at.
        </p>

        <p style={para}>
          Of the {R.cities} cities this site covers, {R.hardness.length} have a utility that publishes a hardness
          figure. At the top, <Link to={cityHref(hardest)} style={link}>{hardest.c}</Link>'s utility
          publishes {hardest.fig} mg/L; at the bottom, <Link to={cityHref(softest)} style={link}>{softest.c}</Link>'s
          publishes {softest.fig}. The same bucket of rinse water behaves differently at each end of that list, which is why
          a method copied from a friend in another city can fail for reasons neither of you can see. The{' '}
          <Link to="/water" style={link}>Water Atlas</Link> explains what each hardness band does to glass; this page
          answers the narrower question of where a city sits against the others.
        </p>

        {/* ── At a glance ─────────────────────────────────────────────── */}
        <section id="at-a-glance" style={{ margin: '36px 0 56px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 28 }}>
            {[['Ten hardest', topTen], ['Ten softest', bottomTen]].map(([title, rows]) => (
              <div key={title}>
                <h2 style={{ ...h2, fontSize: 22 }}>{title}</h2>
                <ol style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                  {rows.map((r) => (
                    <li key={`${r.s}/${r.u}`} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 0', borderTop: `1px solid ${T.rule}`, fontSize: 14 }}>
                      <span><Link to={cityHref(r)} style={{ color: T.ink, textDecoration: 'none' }}>{r.c}</Link><span style={{ color: T.inkFaint }}>, {r.st}</span></span>
                      <span style={{ fontFamily: fontMono, fontSize: 12, color: hardnessColor(r.v, T), whiteSpace: 'nowrap' }}>{r.fig}<Mark basis={r.basis} v={r.basis === 'average' && /\u2013/.test(r.fig) && !/\(/.test(r.fig) ? r.v : null} /></span>
                    </li>
                  ))}
                </ol>
              </div>
            ))}
          </div>
        </section>

        {/* ── Map (s136, Phase 5) ─────────────────────────────────────── */}
        <section id="map" style={{ marginBottom: 56, scrollMarginTop: 24 }}>
          <h2 style={h2}>The map: hardness, dissolved solids, chloramine</h2>
          <p style={para}>
            Each dot is one of the {R.cities} cities, placed at its Census point. On the hardness layer, filled dots are
            coloured by the band of the utility's published figure (a range sits at its middle, as in the table); the
            dissolved-solids layer uses the same 100 and 300 mg/L marks as each city's water panel; the chloramine layer
            shows which disinfectant each utility reports. A hollow dot is a city whose utility publishes nothing on that
            measure. Alaska and Hawaii are drawn as insets.
          </p>
          <Suspense fallback={<div style={{ aspectRatio: '975 / 610', background: T.surface, border: `1px solid ${T.rule}` }} aria-hidden="true" />}>
            <RankingsMap T={T} fontMono={fontMono} fontBody={fontBody} />
          </Suspense>
        </section>

        {/* ── Hardness ─────────────────────────────────────────────────── */}
        <section id="hardness" style={{ marginBottom: 56, scrollMarginTop: 24 }}>
          <h2 style={h2}>Hardness: all {R.hardness.length} ranked cities</h2>
          <p style={para}>
            Figures are mg/L as calcium carbonate, as the utility printed them; grains per gallon is the same number
            divided by 17.12. {midpointCount} utilities publish only a range with no average. Those rank at the middle of
            the range and carry a <span style={{ fontFamily: fontMono, fontSize: 11 }}>mid</span> mark, because the
            midpoint is this page's sort key, not a number the utility reported. A city with a wide range can sit much
            higher or lower for a particular address; the two <em>published</em> sorts rank on the ends of the range
            instead, using only printed numbers.
          </p>
          <SortBar value={hSort} onChange={setHSort} label="Sort hardness table" />
          <div style={{ overflowX: 'auto' }}>
            <table data-region="rank-hardness" style={{ width: '100%', borderCollapse: 'collapse', color: T.ink }}>
              <thead><tr>
                <th style={th}>#</th><th style={th}>CITY</th><th style={th}>PUBLISHED MG/L</th>
                <th style={th} className="wwg-rank-opt">GPG</th><th style={th}>BAND</th><th style={th} className="wwg-rank-opt">UTILITY</th>
              </tr></thead>
              <tbody>
                {hRows.map((r) => (
                  <tr key={`${r.s}/${r.u}`} className="wwg-rank-row">
                    <td style={{ ...td, fontFamily: fontMono, fontSize: 12, color: T.inkFaint }}>{r.rank}</td>
                    <td style={td}><Link to={cityHref(r)} style={{ color: T.ink }}>{r.c}</Link><span style={{ color: T.inkFaint }}>, {r.st}</span></td>
                    <td style={{ ...td, fontFamily: fontMono, fontSize: 12.5, whiteSpace: 'nowrap', color: hardnessColor(r.v, T) }}>{r.fig}<Mark basis={r.basis} v={r.basis === 'average' && /\u2013/.test(r.fig) && !/\(/.test(r.fig) ? r.v : null} /></td>
                    <td style={{ ...td, fontFamily: fontMono, fontSize: 12, color: T.inkSoft }} className="wwg-rank-opt">{mgLToGpg(r.v)}</td>
                    <td style={{ ...td, color: T.inkSoft }}>{r.band}</td>
                    <td style={{ ...td, color: T.inkFaint, fontSize: 13 }} className="wwg-rank-opt">{r.by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3 style={{ fontFamily: fontDisplay, fontSize: 21, fontWeight: 400, margin: '32px 0 8px' }}>
            Not ranked: {R.cities - R.hardness.length} cities
          </h3>
          <p style={para}>
            {nr.unpublished.length} utilities were read and print no hardness figure at all. That is a finding about the
            report, not about the water, and each city page says which document was checked.
          </p>
          <NameList rows={nr.unpublished} />
          {nr.qualitative.length > 0 && (<>
            <p style={para}>{nr.qualitative.length} describe their water in words only, which cannot be ranked against a number:</p>
            <NameList rows={nr.qualitative} word />
          </>)}
          {nr.unlocated.length > 0 && (<>
            <p style={para}>For {nr.unlocated.length} the utility's figure has not been found yet. They stay open rather than filled in with an estimate:</p>
            <NameList rows={nr.unlocated} />
          </>)}
        </section>

        {/* ── TDS ──────────────────────────────────────────────────────── */}
        <section id="tds" style={{ marginBottom: 56, scrollMarginTop: 24 }}>
          <h2 style={h2}>Total dissolved solids: {R.tds.length} ranked cities</h2>
          <p style={para}>
            {FIELD_GUIDE.tds.about} Hardness is only part of that load; sodium, sulfate and chloride dry on glass too, so
            two cities with the same hardness can spot very differently. {tdsOver} of the ranked cities carry the
            “{FIELD_GUIDE.tds.rules[0].flag.toLowerCase()}” flag and {tdsClean} carry
            “{FIELD_GUIDE.tds.rules[1].flag.toLowerCase()}”; the thresholds behind both are on
            the <Link to="/methodology#tds" style={link}>methodology page</Link>.
          </p>
          <p style={para}>
            Fewer utilities report TDS than hardness: {R.tdsNotPublished} of the {R.cities} publish no figure,
            {R.tdsNotRead > 0 ? ` and ${R.tdsNotRead} have not been read yet,` : ''} so this table is shorter. Ranges
            and the <span style={{ fontFamily: fontMono, fontSize: 11 }}>mid</span> mark work as in the hardness table.
          </p>
          <SortBar value={tSort} onChange={setTSort} label="Sort dissolved-solids table" />
          <div style={{ overflowX: 'auto' }}>
            <table data-region="rank-tds" style={{ width: '100%', borderCollapse: 'collapse', color: T.ink }}>
              <thead><tr>
                <th style={th}>#</th><th style={th}>CITY</th><th style={th}>PUBLISHED MG/L</th><th style={th}>AT THE GLASS</th>
                <th style={th} className="wwg-rank-opt">UTILITY</th>
              </tr></thead>
              <tbody>
                {tRows.map((r) => (
                  <tr key={`${r.s}/${r.u}`} className="wwg-rank-row">
                    <td style={{ ...td, fontFamily: fontMono, fontSize: 12, color: T.inkFaint }}>{r.rank}</td>
                    <td style={td}><Link to={cityHref(r)} style={{ color: T.ink }}>{r.c}</Link><span style={{ color: T.inkFaint }}>, {r.st}</span></td>
                    <td style={{ ...td, fontFamily: fontMono, fontSize: 12.5, whiteSpace: 'nowrap' }}>{r.fig}<Mark basis={r.basis} v={r.basis === 'average' && /\u2013/.test(r.fig) && !/\(/.test(r.fig) ? r.v : null} /></td>
                    <td style={{ ...td, color: T.inkSoft }}>{r.flag}</td>
                    <td style={{ ...td, color: T.inkFaint, fontSize: 13 }} className="wwg-rank-opt">{r.by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* ── pH and saturation (s134) ─────────────────────────────────── */}
        <section id="ph" style={{ marginBottom: 56, scrollMarginTop: 24 }}>
          <h2 style={h2}>pH: {R.ph.length} ranked cities, most alkaline first</h2>
          <p style={para}>
            {FIELD_GUIDE.ph.about} {phHigh > 0
              ? `${phHigh} of the ranked cities publish a figure at pH 9 or above, the level this site flags: ${FIELD_GUIDE.ph.rules[0].text.charAt(0).toLowerCase()}${FIELD_GUIDE.ph.rules[0].text.slice(1)}`
              : 'None of the ranked cities publishes a figure at pH 9 or above, the level this site flags for anodised aluminium.'}{' '}
            {R.phNotPublished} utilities publish no pH at all.
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table data-region="rank-ph" style={{ width: '100%', borderCollapse: 'collapse', color: T.ink }}>
              <thead><tr><th style={th}>#</th><th style={th}>CITY</th><th style={th}>PUBLISHED PH</th><th style={th}>AT THE FRAME</th></tr></thead>
              <tbody>
                {R.ph.map((r) => (
                  <tr key={`${r.s}/${r.u}`} className="wwg-rank-row">
                    <td style={{ ...td, fontFamily: fontMono, fontSize: 12, color: T.inkFaint }}>{r.rank}</td>
                    <td style={td}><Link to={cityHref(r)} style={{ color: T.ink }}>{r.c}</Link><span style={{ color: T.inkFaint }}>, {r.st}</span></td>
                    <td style={{ ...td, fontFamily: fontMono, fontSize: 12.5, whiteSpace: 'nowrap' }}>{r.fig}<Mark basis={r.basis} /></td>
                    <td style={{ ...td, color: T.inkSoft }}>{r.flag}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3 style={{ fontFamily: fontDisplay, fontSize: 21, fontWeight: 400, margin: '32px 0 8px' }}>
            Scale-forming or scale-dissolving: the {R.langelier.length} Langelier cities
          </h3>
          <p style={para}>
            {FIELD_GUIDE.index.about} Only {R.langelier.length} utilities publish a Langelier figure, so this is a list rather
            than a national ranking. {lsiDeposit} tend to deposit scale, {lsiDissolve} tend to dissolve it
            {lsiEither ? `, and ${lsiEither} publish a range that crosses zero` : ''}.
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table data-region="rank-langelier" style={{ width: '100%', borderCollapse: 'collapse', color: T.ink }}>
              <thead><tr><th style={th}>CITY</th><th style={th}>LANGELIER INDEX</th><th style={th}>TENDENCY</th></tr></thead>
              <tbody>
                {R.langelier.map((r) => (
                  <tr key={`${r.s}/${r.u}`} className="wwg-rank-row">
                    <td style={td}><Link to={cityHref(r)} style={{ color: T.ink }}>{r.c}</Link><span style={{ color: T.inkFaint }}>, {r.st}</span></td>
                    <td style={{ ...td, fontFamily: fontMono, fontSize: 12.5, whiteSpace: 'nowrap' }}>{r.fig}</td>
                    <td style={{ ...td, color: T.inkSoft }}>{r.tendency}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* ── Chloramine ───────────────────────────────────────────────── */}
        <section id="chloramine" style={{ marginBottom: 56, scrollMarginTop: 24 }}>
          <h2 style={h2}>Chloramine cities: {R.chloramine.length} of {R.cities}</h2>
          <p style={para}>
            {FIELD_GUIDE.disinfectant.about} {FIELD_GUIDE.disinfectant.rules[0].text} That matters most to anyone who
            lets a bucket stand to “air out” before use, and to anyone running a deionising pure-water system, where
            it shortens the life of the resin.
          </p>
          <p style={para}>
            By each utility's own report, {R.chloramine.length} cities in {chloramineStates} states use chloramine
            (alone or after ozone or UV treatment, or for part of the year), {freeChlorineFamily} use free chlorine
            {R.disinfectantCounts['not published'] ? `, and ${R.disinfectantCounts['not published']} do not say` : ''}.
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table data-region="rank-chloramine" style={{ width: '100%', borderCollapse: 'collapse', color: T.ink }}>
              <thead><tr><th style={th}>STATE</th><th style={th}>CITY</th><th style={th}>AS REPORTED</th></tr></thead>
              <tbody>
                {chlorByState.map(([st, rows]) => rows.map((r, i) => (
                  <tr key={`${r.s}/${r.u}`} className="wwg-rank-row">
                    <td style={{ ...td, color: i === 0 ? T.ink : 'transparent', whiteSpace: 'nowrap' }}>{i === 0 ? st : ''}</td>
                    <td style={td}><Link to={cityHref(r)} style={{ color: T.ink }}>{r.c}</Link></td>
                    <td style={{ ...td, color: T.inkSoft }}>{r.type}</td>
                  </tr>
                )))}
              </tbody>
            </table>
          </div>
        </section>

        {/* ── Method ───────────────────────────────────────────────────── */}
        <section id="method" style={{ marginBottom: 56, scrollMarginTop: 24 }}>
          <h2 style={h2}>How these rankings are built</h2>
          <p style={para}>
            Each figure is the one on the city's own page, which names the report, the edition and the table it came
            from. Report years differ from city to city, so two neighbours in the table may be a year or two apart.
            A city ranks on its single published figure where there is one, else on the utility's own average
            (<span style={{ fontFamily: fontMono, fontSize: 11 }}>avg</span>), else on the midpoint of the published range
            (<span style={{ fontFamily: fontMono, fontSize: 11 }}>mid</span>). Cities with the same number share a rank.
          </p>
          <p style={para}>
            Nothing is filled in. A city whose utility does not publish a number is listed as unranked with the reason,
            never given a regional estimate. Band names follow the scale on the{' '}
            <Link to="/methodology#hardness" style={link}>methodology page</Link>, which also sets out what{' '}
            <Link to="/methodology#disinfectant" style={link}>chloramine</Link> and{' '}
            <Link to="/methodology#tds" style={link}>dissolved solids</Link> mean for glass. For the state-level
            picture, start at the <Link to="/water" style={link}>Water Atlas</Link>.
          </p>
          <p style={para} data-region="rank-download">
            The whole table is free to reuse with a link back:{' '}
            <a href={CSV_URL} download style={link}>download it as CSV</a> ({R.cities} rows, one per city: the
            published hardness, dissolved solids and pH with their ranks and ranking basis, the Langelier index where
            published, the disinfectant, and each city's page, where the source document is named).
          </p>
        </section>

        <nav style={{ display: 'flex', flexWrap: 'wrap', gap: 20, paddingTop: 24, borderTop: `1px solid ${T.rule}`, fontFamily: fontMono, fontSize: 11, letterSpacing: '0.12em' }}>
          <Link to="/water" style={{ color: T.inkSoft, textDecoration: 'none' }}>← WATER ATLAS</Link>
          <Link to="/states" style={{ color: T.inkSoft, textDecoration: 'none' }}>CITY AND STATE GUIDES</Link>
          <Link to="/methodology" style={{ color: T.inkSoft, textDecoration: 'none' }}>METHODOLOGY</Link>
          <Link to="/tools/hard-water-scorer" style={{ color: T.inkSoft, textDecoration: 'none' }}>HARD WATER SCORER</Link>
        </nav>
      </div>
    </div>
  );
}
